import { useState, useEffect } from "react";
import {
  getProviderApiCatalog,
  listEndpointFixtures,
  executeTest
} from "../../../services/testExecutionService";
import {
  extractFixtureParameters,
  generateRequestId,
  captureParameterTypes,
  coerceParameterValue
} from "./utils";

// Hook for managing API catalog
export const useCatalog = (isOpen, service) => {
  const [endpoints, setEndpoints] = useState([]);
  // base_url tells the tester which environment a call will hit, and drives the
  // cURL preview. api meta was previously dropped along with the schemas.
  const [apiMeta, setApiMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCatalog = async () => {
      if (isOpen && service && service.api_id) {
        setLoading(true);
        setError(null);

        try {
          const result = await getProviderApiCatalog(service.api_id);

          if (result.success && result.data) {
            const catalogEndpoints = result.data.endpoints || [];
            const formattedEndpoints = catalogEndpoints.map((endpoint) => ({
              id: endpoint.endpoint_id,
              endpoint_id: endpoint.endpoint_id,
              method: endpoint.http_method || "GET",
              path: endpoint.path || endpoint.endpoint_name,
              name: endpoint.endpoint_name,
              description: endpoint.description || "",
              default_fixture: endpoint.default_fixture || null,
              // Carry the schemas through: they decide which control each
              // parameter gets, which fields are required, and what the
              // response is (JSON vs a PDF/HTML document).
              request_schema: endpoint.request_schema || null,
              response_schema: endpoint.response_schema || null
            }));

            setEndpoints(formattedEndpoints);
            setApiMeta({
              api_name: result.data.api_name,
              api_description: result.data.api_description,
              base_url: result.data.base_url,
              version: result.data.version,
              provider_name: result.data.provider_name
            });
          } else {
            setError(result.message || "Failed to load API catalog");
            setEndpoints([]);
            setApiMeta(null);
          }
        } catch (err) {
          console.error("Error fetching catalog:", err);
          setError("Failed to load API catalog");
          setEndpoints([]);
          setApiMeta(null);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchCatalog();
  }, [isOpen, service]);

  return { endpoints, apiMeta, loading, error };
};

// Hook for managing endpoint fixtures
export const useEndpointFixtures = () => {
  const [parameters, setParameters] = useState({});
  const [parameterTypes, setParameterTypes] = useState({});
  const [currentFixtureId, setCurrentFixtureId] = useState(null);
  const [loading, setLoading] = useState(false);

  const applyParams = (params) => {
    setParameters(params || {});
    setParameterTypes(captureParameterTypes(params));
  };

  const fetchFixtures = async (endpoint) => {
    setLoading(true);
    setCurrentFixtureId(null);

    try {
      const result = await listEndpointFixtures(endpoint.endpoint_id);

      if (result.success && result.data && result.data.length > 0) {
        const defaultFixture = result.data[0];
        setCurrentFixtureId(defaultFixture.id);
        const params = extractFixtureParameters(defaultFixture);
        applyParams(params);
      } else if (endpoint.default_fixture) {
        setCurrentFixtureId(endpoint.default_fixture.id);
        const params = extractFixtureParameters(endpoint.default_fixture);
        applyParams(params);
      } else {
        applyParams({});
      }
    } catch (err) {
      console.error("Error fetching endpoint fixtures:", err);
      if (endpoint.default_fixture) {
        setCurrentFixtureId(endpoint.default_fixture.id);
        const params = extractFixtureParameters(endpoint.default_fixture);
        applyParams(params);
      } else {
        applyParams({});
      }
    } finally {
      setLoading(false);
    }
  };

  // Coerce back to the fixture's original JSON type — a text input yields a
  // string for everything, and providers reject mistyped values (Experian:
  // 400 "lienSummary type must be true or false").
  const updateParameter = (key, value) => {
    const coerced = coerceParameterValue(value, parameterTypes[key]);
    setParameters((prev) => ({ ...prev, [key]: coerced }));
  };

  const resetFixtures = () => {
    setParameters({});
    setParameterTypes({});
    setCurrentFixtureId(null);
  };

  return {
    parameters,
    parameterTypes,
    currentFixtureId,
    loading,
    fetchFixtures,
    updateParameter,
    resetFixtures
  };
};

// Hook for test execution
export const useTestExecution = () => {
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const executeApiTest = async (
    selectedEndpoint,
    parameters,
    currentFixtureId
  ) => {
    if (!selectedEndpoint) return;

    setLoading(true);
    setError(null);

    try {
      const testRequest = {
        endpoint_id: selectedEndpoint.endpoint_id,
        request_id: generateRequestId(),
        ...(Object.keys(parameters).length > 0 && {
          request_payload: parameters
        }),
        ...(currentFixtureId && { fixture_id: currentFixtureId })
      };

      const result = await executeTest(testRequest);

      if (result.success && result.data) {
        const responseData = result.data.response_body || result.data;
        const executionTime =
          result.data.duration_ms || result.data.execution_time_ms || "N/A";
        const statusCode =
          result.data.http_status_code || result.data.status_code || 200;

        const formattedResponse = {
          status: statusCode,
          statusText: result.data.status || "OK",
          time: executionTime !== "N/A" ? `${executionTime} ms` : "N/A",
          size: result.data.response_size || "N/A",
          data: responseData
        };

        setResponse(formattedResponse);
        return formattedResponse;
      } else {
        const errorMsg = result.message || "Failed to execute test";
        setError(errorMsg);
        throw new Error(errorMsg);
      }
    } catch (err) {
      console.error("Error executing test:", err);
      // Keep the backend's own message (provider validation errors, auth
      // failures, timeouts). The previous blanket "Failed to execute test
      // request" hid every cause and made failures undiagnosable from the UI.
      const errorMsg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to execute test request";
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearResponse = () => {
    setResponse(null);
    setError(null);
  };

  return { response, loading, error, executeApiTest, clearResponse };
};
